import HttpCodes from 'http-status-codes';
import _ from 'lodash';
import { sqWorkbenchStore, sqWorksheetStore } from '@/core/core.stores';
import { Store } from '@/core/flux.service';
import { formatTime, parseISODate } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { APPSERVER_API_PREFIX, GUID_REGEX_PATTERN } from '@/main/app.constants';
import { BulkEditMode } from '@/reportEditor/components/reportContentModal/bulkEdit/reportContent.constants';
import {
  CONTENT_WARNING_STATUS,
  KEEP_CURRENT_ASSET_SELECTION,
  KEEP_CURRENT_DATE_RANGE,
  KEEP_CURRENT_INTERACTIVE,
  KEEP_CURRENT_KEY,
  KEEP_CURRENT_SCALE,
  KEEP_CURRENT_SHAPE,
  KEEP_CURRENT_SIZE,
  KEEP_CURRENT_SUMMARY,
  QUARTZ_CRON_PENDING_INPUT,
  ReportEditingState,
  ReportEditingStateEvent,
} from '@/reportEditor/report.constants';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { formatAsQueryString } from '@/utilities/httpHelpers.utilities';
import { areAssetSelectionsSimilar, areDateRangesSimilar } from '@/utilities/utilities';
import type { InitializeMode, PersistenceLevel } from '@/core/flux.service';
import type {
  AssetSelection,
  Content,
  ContentDisplayMetadata,
  DateRange,
  DateRangeSwapInfo,
  DecoratedContent,
  InteractiveReportContent,
  ReportContentScale,
  ReportContentShape,
  ReportContentSize,
  ReportContentSummary,
  ReportSchedule,
  RowSelection,
  SandboxMode,
} from '@/reportEditor/report.constants';
import type { AnnotationOutputV1 } from '@seeqdev/api/model/AnnotationOutputV1';
import type { DocumentBackupOutputV1 } from '@seeqdev/api/model/DocumentBackupOutputV1';
import type { ItemPreviewV1 } from '@seeqdev/api/model/ItemPreviewV1';

const IMAGE_SRC_REGEX = new RegExp(`src="${APPSERVER_API_PREFIX}/content/(${GUID_REGEX_PATTERN})/image"`, 'g');

type BackupWithFormattedDate = DocumentBackupOutputV1 & { formattedBackupDate: string };

type BackupPreview = { backupName: string; document: string; formattedBackupDate?: string };

interface ReportState {
  imageStateChanges: number;
  iframeRefreshCounter: number;
  dateRanges: DateRange[];
  content: Content[];
  contentErrors: string[];
  assetSelections: AssetSelection[];
  rowSelections: RowSelection[];
  comments: AnnotationOutputV1[];
  reportSchedule: ReportSchedule | undefined;
  nextRunTime: string | undefined;
  scheduledUpdateCount: number | undefined;
  dateRangesNotArchived: any; // Computed via monkey()
  contentNotArchived: any; // Computed via monkey()
  assetSelectionsNotArchived: any; // Computed via monkey()
  rowSelectionsNotArchived: any; // Computed via monkey()
  hasReportSchedule: any; // Computed via monkey()
  hasDateRangeSchedule: any; // Computed via monkey()
  hasMultipleSchedules: any; // Computed via monkey()
  isScheduleEnabled: any; // Computed via monkey()
  hasAutoDateRanges: any; // Computed via monkey()
  hasFixedDateRanges: any; // Computed via monkey()
  hasLiveOrScheduledContent: any; // Computed via monkey()
  liveOrScheduledContent: any; // Computed via monkey()
  hasLiveContent: any; // Computed via monkey()
  hasFixedContent: any; // Computed via monkey()
  dateRangeToContentMap: any; // Computed via monkey()
  assetSelectionToContentMap: any; // Computed via monkey()
  rowSelectionToContentMap: any; // Computed via monkey()
  backups: BackupWithFormattedDate[];
  id?: string;
  editingState: ReportEditingState;
  isFixedWidth: boolean;
  bulkEditDisplayMode: BulkEditMode;
  bulkInteractive: InteractiveReportContent;
  bulkShape: ReportContentShape;
  bulkScale: ReportContentScale;
  bulkSize: ReportContentSize;
  bulkDateRange: DateRange | undefined;
  bulkWidth: number;
  bulkHeight: number;
  bulkSummary: ReportContentSummary;
  bulkHideUncertainty: boolean | null | undefined;
  bulkAssetSelection: AssetSelection;
  /** Row selection to apply in bulk: an ID, `undefined` to remove row selections, or KEEP_CURRENT_KEY to leave them */
  bulkRowSelectionId: string | undefined;
  /**
   * Which bulk selection is set to remove only because the other one was chosen (content cannot use an asset selection
   * and a row selection at once), so it can go back to keep current when that choice is undone.
   */
  bulkImpliedSelectionRemoval: 'asset' | 'row' | undefined;
  shouldUpdateBulkWorkstep: boolean;
  selectedBulkContent: DecoratedContent[];
  shouldShowConfigureAutoUpdateModal: boolean;
  showConfigureAutoUpdateModal: boolean;
  reportScheduleOverride: boolean;
  contentDisplayMetadata: ContentDisplayMetadata[];
  /** Content that loads even while it is out of view, rather than waiting to be scrolled near. */
  contentIdsToLoadOutOfView: string[];
  contentShowWarningMessage: Record<string, boolean>;
  isSandboxDisabled: boolean;
  sandboxMode: SandboxMode;
  activeDateRangeSwapInfo: {
    currentSwap: DateRangeSwapInfo | undefined;
    potentialSwaps: DateRangeSwapInfo[];
  };
  showChooseAssetSwapModal: boolean;
  showChooseCapsuleModal: boolean;
  name?: string;
  createdBy?: ItemPreviewV1;
  renderer?: ItemPreviewV1;
  createdAt?: string;
  updatedAt?: string;
  document?: string;
  isCkEnabled?: boolean;
  dateRangeUpdating: boolean;
  backupPreview?: BackupPreview;
  reportScheduleError?: { error: string; errorCode: number };
}

export class ReportStore extends Store {
  static readonly storeName = 'sqReportStore';
  persistenceLevel: PersistenceLevel = 'NONE';

  initialize(initializeMode: InitializeMode): void {
    const saveState = this.state && initializeMode !== 'FORCE';
    const initialState: ReportState = {
      imageStateChanges: 0, // Let listeners know that the report changed (e.g. image state changes)
      iframeRefreshCounter: 0, // Track when iframes need refreshing if link to date ranges and/or asset selections
      dateRanges: [],
      content: [],
      contentErrors: [],
      assetSelections: [],
      rowSelections: [],
      comments: [],
      reportSchedule: undefined,
      nextRunTime: undefined,
      scheduledUpdateCount: undefined,
      dateRangesNotArchived: this.monkey(['dateRanges', (dateRanges) => _.reject(dateRanges, 'isArchived')]),
      contentNotArchived: this.monkey(['content', (content) => _.reject(content, 'isArchived')]),
      assetSelectionsNotArchived: this.monkey(['assetSelections', (selection) => _.reject(selection, 'isArchived')]),
      rowSelectionsNotArchived: this.monkey([
        'rowSelections',
        (selections: RowSelection[]) => selections.filter((selection) => !selection.isArchived),
      ]),
      hasReportSchedule: this.monkey(['reportSchedule'], (reportSchedule) => !_.isEmpty(reportSchedule?.cronSchedule)),
      hasDateRangeSchedule: this.monkey(['dateRangesNotArchived'], (dateRanges: DateRange[]) =>
        _.some(dateRanges, (dateRange) => {
          const schedule = _.get(dateRange, 'auto.cronSchedule');
          return schedule && !_.isEmpty(schedule) && !_.isEqual(schedule, [QUARTZ_CRON_PENDING_INPUT]);
        }),
      ),
      hasMultipleSchedules: this.monkey(
        ['hasReportSchedule'],
        ['dateRangesNotArchived'],
        (hasReportSchedule: boolean, dateRanges: DateRange[]) => {
          if (hasReportSchedule) return false;

          return (
            _.chain(dateRanges)
              .filter((dateRange) => dateRange?.auto?.enabled && !_.isEmpty(dateRange?.auto?.cronSchedule))
              .map((dateRange) => dateRange?.auto?.cronSchedule?.slice(0)?.sort()?.join('|')) // join array as
              // string for easier comparison
              .thru((schedules) => new Set(schedules))
              .value().size > 1
          );
        },
      ),
      isScheduleEnabled: this.monkey(
        ['hasReportSchedule'],
        ['reportSchedule'],
        ['hasDateRangeSchedule'],
        ['dateRangesNotArchived'],
        (hasReportSchedule: boolean, reportSchedule: any, hasDateRangeSchedule: boolean, dateRanges: DateRange[]) => {
          const scheduleConfigured = hasReportSchedule || hasDateRangeSchedule;
          if (!scheduleConfigured) {
            // If a schedule isn't configured yet, then we shouldn't consider the schedule "disabled".
            return true;
          } else if (hasReportSchedule) {
            return reportSchedule.enabled;
          } else if (hasDateRangeSchedule) {
            return _.every(dateRanges, (dateRange) => dateRange?.enabled);
          }
        },
      ),
      hasAutoDateRanges: this.monkey(['dateRangesNotArchived'], (dateRanges: DateRange[]) => {
        return !_.isEmpty(dateRanges.filter((dateRange) => dateRange?.auto?.enabled));
      }),
      hasFixedDateRanges: this.monkey(['dateRangesNotArchived'], (dateRanges: DateRange[]) => {
        return !_.chain(dateRanges)
          .reject((dateRange) => dateRange?.auto?.enabled)
          .isEmpty()
          .value();
      }),
      hasLiveOrScheduledContent: this.monkey(
        ['liveOrScheduledContent'],
        (liveOrScheduledContent: Content[]) => liveOrScheduledContent.length > 0,
      ),
      liveOrScheduledContent: this.monkey(
        ['contentNotArchived'],
        ['dateRangesNotArchived'],
        (content: Content[], dateRanges: DateRange[]): Content[] => {
          return _.chain(dateRanges)
            .filter((dateRange) => dateRange?.auto?.enabled)
            .map((dateRanges) => _.filter(content, ['dateRangeId', dateRanges.id]))
            .flatten()
            .value();
        },
      ),
      hasLiveContent: this.monkey(
        ['contentNotArchived'],
        ['dateRangesNotArchived'],
        ['reportSchedule'],
        ['hasReportSchedule'],
        ['isScheduleEnabled'],
        (content: Content[], dateRanges: DateRange[], reportSchedule, hasReportSchedule, isScheduleEnabled) => {
          if (!hasReportSchedule || reportSchedule.background || !isScheduleEnabled) {
            return false;
          }
          return (
            _.chain(dateRanges)
              .filter((dateRange) => dateRange?.auto?.enabled)
              .map((dateRanges) => _.filter(content, ['dateRangeId', dateRanges.id]))
              .flatten()
              .value().length > 0
          );
        },
      ),
      hasFixedContent: this.monkey(
        ['contentNotArchived'],
        ['dateRangesNotArchived'],
        (content: Content[], dateRanges: DateRange[]) => {
          return (
            _.chain(dateRanges)
              .reject((dateRange) => dateRange?.auto?.enabled)
              .map((dateRanges: any) => _.filter(content, ['dateRangeId', dateRanges.id]))
              .flatten()
              .value().length > 0
          );
        },
      ),
      dateRangeToContentMap: this.monkey(['contentNotArchived'], (content) => {
        return _.groupBy(content, (content) => (content.dateRangeId ? content.dateRangeId : 'none'));
      }),
      assetSelectionToContentMap: this.monkey(['contentNotArchived'], (content) => {
        return _.groupBy(content, (content) => (content.assetSelectionId ? content.assetSelectionId : 'none'));
      }),
      rowSelectionToContentMap: this.monkey(['contentNotArchived'], (content: Content[]) =>
        _.groupBy(content, (content) => content.rowSelectionId ?? 'none'),
      ),
      backups: [],
      // In fast-follow mode we have to keep this id around so that we can make a request for the rest of the data
      id: saveState ? this.state.get('id') : undefined,
      editingState: ReportEditingState.Saved,
      isFixedWidth: false,
      bulkEditDisplayMode: BulkEditMode.DO_NOT_SHOW,
      bulkInteractive: KEEP_CURRENT_INTERACTIVE,
      bulkShape: KEEP_CURRENT_SHAPE,
      bulkScale: KEEP_CURRENT_SCALE,
      bulkSize: KEEP_CURRENT_SIZE,
      bulkDateRange: KEEP_CURRENT_DATE_RANGE,
      bulkWidth: 500,
      bulkHeight: 500,
      bulkSummary: KEEP_CURRENT_SUMMARY,
      bulkHideUncertainty: undefined,
      bulkAssetSelection: KEEP_CURRENT_ASSET_SELECTION,
      bulkRowSelectionId: KEEP_CURRENT_KEY,
      bulkImpliedSelectionRemoval: undefined,
      shouldUpdateBulkWorkstep: false,
      selectedBulkContent: [],
      shouldShowConfigureAutoUpdateModal: false,
      showConfigureAutoUpdateModal: false,
      reportScheduleOverride: false,
      contentDisplayMetadata: [],
      contentIdsToLoadOutOfView: [],
      contentShowWarningMessage: {},
      isSandboxDisabled: false,
      sandboxMode: {
        enabled: false,
        isModified: false,
        originalReportId: null,
        originalWorksheetId: null,
        sandboxedWorksheetId: null,
        sandboxedWorkbookId: null,
        useRendererUser: false,
      },
      activeDateRangeSwapInfo: {
        currentSwap: undefined,
        potentialSwaps: [],
      },
      showChooseAssetSwapModal: false,
      showChooseCapsuleModal: false,
      createdBy: undefined,
      renderer: undefined,
      createdAt: undefined,
      updatedAt: undefined,
      document: undefined,
      isCkEnabled: false,
      dateRangeUpdating: false,
      backupPreview: undefined,
      reportScheduleError: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get isSandboxDisabled(): boolean {
    return this.state.get('isSandboxDisabled');
  }

  get sandboxMode(): SandboxMode {
    return this.state.get('sandboxMode');
  }

  get assetSelections(): AssetSelection[] {
    return this.state.get('assetSelections');
  }

  get assetSelectionsNotArchived(): AssetSelection[] {
    return this.state.get('assetSelectionsNotArchived');
  }

  get rowSelections(): RowSelection[] {
    return this.state.get('rowSelections');
  }

  get rowSelectionsNotArchived(): RowSelection[] {
    return this.state.get('rowSelectionsNotArchived');
  }

  get id(): string | undefined {
    return this.state.get('id');
  }

  get createdBy(): ItemPreviewV1 | undefined {
    return this.state.get('createdBy');
  }

  get renderer(): ItemPreviewV1 | undefined {
    return this.state.get('renderer');
  }

  get createdAt(): string | undefined {
    return this.state.get('createdAt');
  }

  get updatedAt(): string | undefined {
    return this.state.get('updatedAt');
  }

  get document(): string | undefined {
    return this.state.get('document');
  }

  get comments(): AnnotationOutputV1[] {
    return this.state.get('comments');
  }

  get nextRunTime(): string | undefined {
    return this.state.get('nextRunTime');
  }

  get scheduledUpdateCount(): number | undefined {
    return this.state.get('scheduledUpdateCount');
  }

  get content(): Content[] {
    return this.state.get('content');
  }

  get contentErrors(): string[] {
    return this.state.get('contentErrors');
  }

  get contentDisplayMetadata(): ContentDisplayMetadata[] {
    return this.state.get('contentDisplayMetadata');
  }

  get contentIdsToLoadOutOfView(): string[] {
    return this.state.get('contentIdsToLoadOutOfView');
  }

  get forbiddenContentErrorsCount(): number {
    return _.filter(this.state.get('contentDisplayMetadata'), {
      errorCode: HttpCodes.FORBIDDEN,
    }).length;
  }

  get isReportScheduleError(): boolean {
    return !_.isEmpty(this.state.get('reportScheduleError'));
  }

  get contentNotArchived(): Content[] {
    return this.state.get('contentNotArchived');
  }

  get contentWorkbookIds(): string[] {
    return _.chain(this.state.get('contentNotArchived'))
      .map('workbookId')
      .concat(sqWorkbenchStore.stateParams.workbookId)
      .uniq()
      .value();
  }

  get dateRanges(): DateRange[] {
    return this.state.get('dateRanges');
  }

  get dateRangesNotArchived(): DateRange[] {
    return this.state.get('dateRangesNotArchived');
  }

  get backups(): BackupWithFormattedDate[] {
    return this.state.get('backups');
  }

  get backupPreview(): BackupPreview | undefined {
    return this.state.get('backupPreview');
  }

  get dateRangeUpdating(): boolean {
    return this.state.get('dateRangeUpdating');
  }

  get editingState(): ReportEditingState {
    return this.state.get('editingState');
  }

  getContentById(contentId: string | undefined): Content | undefined {
    return _.find(this.state.get('content'), ['id', contentId]);
  }

  getDateRangeById(dateRangeId: string | undefined): DateRange | undefined {
    return _.find(this.state.get('dateRanges'), ['id', dateRangeId]);
  }

  isDateRangeUsed(dateRangeId: string): boolean {
    return !_.isEmpty(this.contentUsingDateRange(dateRangeId)) || this.hasIframesUsingDateRange(dateRangeId);
  }

  hasInUseDateRanges(): boolean {
    const ranges = this.dateRanges;
    const dateRangeToContentMap = this.dateRangeToContentMap;
    return (
      _.some(ranges, (dateRange) => _.get(dateRangeToContentMap, dateRange.id, []).length > 0) ||
      _.some(ranges, (dateRange) => this.hasIframesUsingDateRange(dateRange.id))
    );
  }

  isContentGenerating(contentId: string): boolean {
    return _.get(_.find(this.state.get('content'), ['id', contentId]), 'isGenerating');
  }

  isAssetSelectionUsed(assetSelectionId: string): boolean {
    return (
      !_.isEmpty(this.contentUsingAssetSelection(assetSelectionId)) ||
      this.hasIframesUsingAssetSelection(assetSelectionId)
    );
  }

  isRowSelectionUsed(rowSelectionId: string): boolean {
    return this.contentUsingRowSelection(rowSelectionId).length > 0;
  }

  hasInUseRowSelections(): boolean {
    return this.rowSelectionsNotArchived.some(
      (selection) => !!selection.id && this.contentUsingRowSelection(selection.id).length > 0,
    );
  }

  hasInUseAssetSelections(): boolean {
    const selections = this.assetSelections;
    const selectionToContentMap = this.assetSelectionToContentMap;
    return (
      _.some(selections, (selection) => selection.id && _.get(selectionToContentMap, selection.id, []).length > 0) ||
      _.some(selections, (selection) => selection.id && this.hasIframesUsingAssetSelection(selection.id))
    );
  }

  /**
   * Two date ranges are considered similar if they have matching conditions, matching durations, and both are
   * auto or both are not auto. To prevent duplication, dateRanges with different ranges (but same duration),
   * different offset, different auto.chronSchedule, different auto.background, etc., are all considered similar.
   *
   * @param dateRange - The date range we want to find a suitable alternative for
   * @returns - A similar dateRange if it exists, otherwise undefined.
   */
  findSimilarDateRange(dateRange: DateRange): DateRange | undefined {
    return _.find(this.state.get('dateRanges'), (dr) => areDateRangesSimilar(dr, dateRange));
  }

  /**
   * Sometimes we need to find a daterange that has the same name (switch to sandbox mode)
   * @param name name to search by
   * @returns
   */

  findDateRangeByName(name: string): DateRange | undefined {
    return _.find(this.state.get('dateRanges'), ['name', name]);
  }

  /**
   * Finds the first asset selection which has the same assetId. Archived asset selections are ignored.
   * @param assetSelection - the source asset selection
   * @returns - The closest asset selection if exists, else undefined
   */
  findSimilarAssetSelection(assetSelection: AssetSelection): AssetSelection | undefined {
    const assetSelections = this.state.get('assetSelections');
    return _.find(assetSelections, (sel) => areAssetSelectionsSimilar(assetSelection, sel));
  }

  /**
   * Computes a non conflicting asset selection name by adding '(n)' to the name if the asset selection name
   * already exits.
   * @param assetSelectionName - the asset selection name
   * @returns - The original name if it does not exist, otherwise the name with a '(n)' suffix.
   */
  computeNonConflictingAssetSelectionName(assetSelectionName: string): string {
    const assetSelectionNames = _.map(this.state.get('assetSelections'), (selection) => selection.name);

    let index = 1;
    let nonConflictingName = assetSelectionName;
    while (_.some(assetSelectionNames, (name) => name === nonConflictingName)) {
      nonConflictingName = `${assetSelectionName} (${index++})`;
    }

    return nonConflictingName;
  }

  isDateRangeUpdating(dateRangeId: string): boolean {
    return _.get(_.find(this.state.get('dateRanges'), ['id', dateRangeId]), 'auto.enabled', false);
  }

  get hasMultipleSchedules(): boolean {
    return this.state.get('hasMultipleSchedules');
  }

  get reportSchedule(): ReportSchedule | undefined {
    return this.state.get('reportSchedule');
  }

  get hasReportSchedule(): boolean {
    return this.state.get('hasReportSchedule');
  }

  get isScheduleEnabled(): boolean {
    return this.state.get('isScheduleEnabled');
  }

  get hasFixedDateRanges(): boolean {
    return this.state.get('hasFixedDateRanges');
  }

  get hasAutoDateRanges(): boolean {
    return this.state.get('hasAutoDateRanges');
  }

  get liveOrScheduledContent(): Content[] {
    return this.state.get('liveOrScheduledContent');
  }

  get hasLiveOrScheduledContent(): boolean {
    return this.state.get('hasLiveOrScheduledContent');
  }

  get hasLiveContent(): boolean {
    return this.state.get('hasLiveContent');
  }

  get hasFixedContent(): boolean {
    return this.state.get('hasFixedContent');
  }

  get dateRangeToContentMap(): Record<string, Content[]> {
    return this.state.get('dateRangeToContentMap');
  }

  get assetSelectionToContentMap(): Record<string, Content[]> {
    return this.state.get('assetSelectionToContentMap');
  }

  contentUsingAssetSelection(assetSelectionId = 'none'): Content[] {
    return _.get(this.assetSelectionToContentMap, assetSelectionId, []);
  }

  get rowSelectionToContentMap(): Record<string, Content[]> {
    return this.state.get('rowSelectionToContentMap');
  }

  contentUsingRowSelection(rowSelectionId: string): Content[] {
    return this.rowSelectionToContentMap[rowSelectionId] ?? [];
  }

  contentUsingDateRange(dateRangeId = 'none'): Content[] {
    return _.get(this.dateRangeToContentMap, dateRangeId, []);
  }

  hasIframesUsingAssetSelection(assetSelectionId: string): boolean {
    const document: string | null | undefined = this.state.get('document');
    const matches = document?.match(
      new RegExp(`data-iframe-content-url=".*{assetSelection:\\s*${assetSelectionId}[a-zA-Z0-9_\\s:]+}`, 'gi'),
    );
    return !_.isEmpty(matches);
  }

  hasIframesUsingDateRange(dateRangeId: string): boolean {
    const document: string | null | undefined = this.state.get('document');
    const matches = document?.match(
      new RegExp(`data-iframe-content-url=".*{dateRange(Start|End)*:s*${dateRangeId}[a-zA-Z0-9_\\s:]+}`, 'gi'),
    );
    return !_.isEmpty(matches);
  }

  hasIframesUsingAutoDateRange(): boolean {
    return this.dateRanges.some((range) => range.auto && this.hasIframesUsingDateRange(range.id));
  }

  get iframeRefreshCounter(): number {
    return this.state.get('iframeRefreshCounter');
  }

  getWorksheetUrl(contentId: string): string {
    return `${APPSERVER_API_PREFIX}/content/${contentId}/sourceUrl`;
  }

  get hasContentWarnings(): boolean {
    return _.some(this.state.get('contentNotArchived'), 'screenshotWarning');
  }

  get numContentWarnings(): number {
    return _.filter(this.state.get('contentNotArchived'), 'screenshotWarning').length;
  }

  /**
   * Fixed width mode is not used when capturing PDFs in headless mode because it is not needed since the margins
   * are added by puppeteer. Additionally, fixed with mode creates an invisible copy of the document in a hidden
   * node which makes it more expensive. It also causes interactive trends to not fill the whole table cell (CRAB-38446)
   * and ag-grid charts may not finish rendering (CRAB-49715).
   */
  get isFixedWidth(): boolean {
    return headlessRenderMode() ? false : this.state.get('isFixedWidth');
  }

  get isCkEnabled(): boolean {
    return this.state.get('isCkEnabled');
  }

  get isBulkEditAdvancedOnly(): boolean {
    return this.state.get('bulkEditDisplayMode') === BulkEditMode.ADVANCED_ONLY;
  }

  get bulkEditDisplayMode(): BulkEditMode {
    return this.state.get('bulkEditDisplayMode');
  }

  get showBulkEditModal(): boolean {
    return this.state.get('bulkEditDisplayMode') !== BulkEditMode.DO_NOT_SHOW;
  }

  get bulkInteractive(): InteractiveReportContent {
    return this.state.get('bulkInteractive');
  }

  get bulkScale(): ReportContentScale {
    return this.state.get('bulkScale');
  }

  get bulkShape(): ReportContentShape {
    return this.state.get('bulkShape');
  }

  get bulkSize(): ReportContentSize {
    return this.state.get('bulkSize');
  }

  get bulkWidth(): number {
    return this.state.get('bulkWidth');
  }

  get bulkHeight(): number {
    return this.state.get('bulkHeight');
  }

  get bulkDateRange(): DateRange | undefined {
    return this.state.get('bulkDateRange');
  }

  get bulkSummary(): ReportContentSummary {
    return this.state.get('bulkSummary');
  }

  get bulkHideUncertainty(): boolean | null | undefined {
    return this.state.get('bulkHideUncertainty');
  }

  get bulkAssetSelection(): AssetSelection {
    return this.state.get('bulkAssetSelection');
  }

  get bulkRowSelectionId(): string | undefined {
    return this.state.get('bulkRowSelectionId');
  }

  get bulkImpliedSelectionRemoval(): 'asset' | 'row' | undefined {
    return this.state.get('bulkImpliedSelectionRemoval');
  }

  get shouldUpdateBulkWorkstep(): boolean {
    return this.state.get('shouldUpdateBulkWorkstep');
  }

  get selectedBulkContent(): DecoratedContent[] {
    return this.state.get('selectedBulkContent');
  }

  get shouldShowConfigureAutoUpdateModal(): boolean {
    return this.state.get('shouldShowConfigureAutoUpdateModal');
  }

  get showConfigureAutoUpdateModal(): boolean {
    return this.state.get('showConfigureAutoUpdateModal');
  }

  get showChooseAssetSwapModal(): boolean {
    return this.state.get('showChooseAssetSwapModal');
  }

  get activeDateRangeSwapInfo(): any {
    return this.state.get('activeDateRangeSwapInfo');
  }

  get showChooseCapsuleModal(): boolean {
    return this.state.get('showChooseCapsuleModal');
  }

  get reportScheduleOverride(): boolean {
    return this.state.get('reportScheduleOverride');
  }

  getContentDisplayMetadataById(contentId: string): ContentDisplayMetadata {
    return _.find(this.state.get('contentDisplayMetadata'), ['contentId', contentId]);
  }

  hasShowWarningMessage(contentId: string): boolean {
    return this.state.get('contentShowWarningMessage', contentId) ?? false;
  }

  getAssetSelectionById(assetSelectionId: string) {
    return _.find(this.state.get('assetSelections'), ['id', assetSelectionId]);
  }

  getRowSelectionById(rowSelectionId: string): RowSelection | undefined {
    return this.rowSelections.find((selection) => selection.id === rowSelectionId);
  }

  get contentWarningStatus(): CONTENT_WARNING_STATUS {
    const someExpanded = _.some(this.state.get('contentNotArchived'), (content) =>
      content?.id ? this.state.get('contentShowWarningMessage', content.id) : false,
    );

    const someCollapsed = _.some(this.state.get('contentNotArchived'), (content) =>
      content?.id ? !this.state.get('contentShowWarningMessage', content.id) : true,
    );

    if (someExpanded && someCollapsed) {
      return CONTENT_WARNING_STATUS.MIXED;
    } else if (someExpanded) {
      return CONTENT_WARNING_STATUS.MESSAGES;
    } else {
      return CONTENT_WARNING_STATUS.ICONS;
    }
  }

  /**
   * Sets the data in the state tree if the data and the current state fail a deep equality check. This should be
   * used for data that is pushed into the store frequently, but which is likely not to be changed (such as after
   * each save of a document as a user types). The benefit of not setting data that hasn't changed is that React
   * components (which rely on shallow object comparison) won't have a re-render triggered. While there is some
   * cost to the deep equality check, performance tests show it is faster or the same as the time to set the data
   * in the immutable store.
   *
   * @param stateKey - The key in the state tree to get/set the data
   * @param data - The new data to set, if different from current data
   */
  setIfNotEqual(stateKey: string, data: any) {
    if (!_.isEqual(this.state.get(stateKey), data)) {
      this.state.set(stateKey, data);
    }
  }

  /**
   * Generates an image URL for a piece of content that allows for the image to be fetched async and that solves
   * caching issues. The hash code as a query param is necessary to ensure that the image is not re-fetched
   * needlessly, such as when only the text has been updated in a concurrent editing situation. However, if the image
   * has been updated then the hash code will change and thus the image will be forced to be re-fetched.
   *
   * @param contentId - The id of the content
   * @param useAsync - If the image should be fetched async. Should only be used by the preview window
   * which does not have the problem on bottlenecking on concurrent requests to the same host.
   * @param ignoreHashCode - Whether or not to ignore the hashcode. This can be used to always get a consistent URL
   * for a piece of content
   */
  getContentImageUrl(contentId: string, useAsync = true, ignoreHashCode = false): string {
    const content: Content = _.find(this.state.get('content'), ['id', contentId]);
    const hashCode = content?.hashCode;
    const params = formatAsQueryString({
      useAsync: useAsync ? true : undefined,
      hash: ignoreHashCode ? undefined : hashCode,
    });

    return `${APPSERVER_API_PREFIX}/content/${contentId}/${content?.isReact ? 'react' : 'image'}${
      _.isEmpty(params) ? '' : `?${params}`
    }`;
  }

  /**
   * Keeps the generating state of content that a live screenshot message set, unless a snapshot of the content was
   * requested after that. A snapshot's isGenerating is whether a job existed for the content when the backend read it,
   * which can be older than the messages that already updated the store, such as when a job finishes while the
   * document is being saved. A message that was missed, on the other hand, leaves a state that only a later snapshot
   * can correct.
   *
   * @param content - The content from the snapshot
   * @param requestedAt - When the snapshot was requested, if known
   */
  private withLiveGeneratingState(content: Content, requestedAt?: number): Content {
    const existingContent: Content | undefined = _.find(this.state.get('content'), ['id', content.id]);
    if (existingContent?.generatingUpdatedAt === undefined) {
      return content;
    }
    const { isGenerating, generatingUpdatedAt } = existingContent;
    return requestedAt === undefined || generatingUpdatedAt >= requestedAt
      ? { ...content, isGenerating, generatingUpdatedAt }
      : content;
  }

  /**
   * Sets state that indicates that the user has changed an asset selection or date range while in sandbox mode.
   */
  private markSandboxModeModified() {
    this.state.set(['sandboxMode', 'isModified'], true);
  }

  /**
   * Sets the row selection to apply in bulk. Choosing one removes the bulk asset selection, because content cannot use
   * both, and going back to keep current or remove restores an asset selection that was removed only for that reason.
   * An asset selection the user had already chosen to remove stays removed. Keeping the current row selections also
   * keeps the current asset selections, since attaching an asset selection would remove those row selections.
   */
  private setBulkRowSelectionId(bulkRowSelectionId: string | undefined) {
    this.state.set('bulkRowSelectionId', bulkRowSelectionId);
    if (this.state.get('bulkImpliedSelectionRemoval') === 'row') {
      this.state.set('bulkImpliedSelectionRemoval', undefined);
    }
    if (bulkRowSelectionId && bulkRowSelectionId !== KEEP_CURRENT_KEY) {
      // An undefined bulk asset selection removes the asset selection
      if (!_.isUndefined(this.state.get('bulkAssetSelection'))) {
        this.state.set('bulkAssetSelection', undefined);
        this.state.set('bulkImpliedSelectionRemoval', 'asset');
      }
    } else if (this.state.get('bulkImpliedSelectionRemoval') === 'asset') {
      this.state.set('bulkAssetSelection', KEEP_CURRENT_ASSET_SELECTION);
      this.state.set('bulkImpliedSelectionRemoval', undefined);
    } else if (bulkRowSelectionId === KEEP_CURRENT_KEY && this.isBulkAssetSelectionAttached()) {
      this.state.set('bulkAssetSelection', KEEP_CURRENT_ASSET_SELECTION);
    }
  }

  private isBulkAssetSelectionAttached(): boolean {
    const bulkAssetSelection = this.state.get('bulkAssetSelection');
    return !_.isUndefined(bulkAssetSelection) && bulkAssetSelection.id !== KEEP_CURRENT_ASSET_SELECTION.id;
  }

  /** Bulk edit must not attach an archived row selection, which the backend rejects */
  private keepCurrentBulkRowSelectionIfArchived() {
    const bulkRowSelectionId = this.state.get('bulkRowSelectionId');
    if (
      bulkRowSelectionId &&
      bulkRowSelectionId !== KEEP_CURRENT_KEY &&
      this.getRowSelectionById(bulkRowSelectionId)?.isArchived
    ) {
      this.setBulkRowSelectionId(KEEP_CURRENT_KEY);
    }
  }

  protected readonly handlers = {
    REPORT_SET_BULK_EDIT_DISPLAY_MODE: ({ bulkEditDisplayMode }: { bulkEditDisplayMode: BulkEditMode }) => {
      this.state.set('bulkEditDisplayMode', bulkEditDisplayMode);
    },

    REPORT_SET_BULK_INTERACTIVE: ({ bulkInteractive }: { bulkInteractive: InteractiveReportContent }) => {
      this.state.set('bulkInteractive', bulkInteractive);
    },

    REPORT_SET_BULK_SHAPE: ({ bulkShape }: { bulkShape: ReportContentShape }) => {
      this.state.set('bulkShape', bulkShape);
    },

    REPORT_SET_BULK_SIZE: ({ bulkSize }: { bulkSize: ReportContentSize }) => {
      this.state.set('bulkSize', bulkSize);
    },

    REPORT_SET_BULK_SCALE: ({ bulkScale }: { bulkScale: ReportContentScale }) => {
      this.state.set('bulkScale', bulkScale);
    },

    REPORT_SET_BULK_HEIGHT: ({ bulkHeight }: { bulkHeight: number }) => {
      this.state.set('bulkHeight', bulkHeight);
    },

    REPORT_SET_BULK_WIDTH: ({ bulkWidth }: { bulkWidth: number }) => {
      this.state.set('bulkWidth', bulkWidth);
    },

    REPORT_SET_BULK_DATE_RANGE: ({ bulkDateRange }: { bulkDateRange: DateRange | undefined }) => {
      this.state.set('bulkDateRange', bulkDateRange);
    },

    REPORT_SET_SHOULD_UPDATE_BULK_WORKSTEP: ({ shouldUpdateBulkWorkstep }: { shouldUpdateBulkWorkstep: boolean }) => {
      this.state.set('shouldUpdateBulkWorkstep', shouldUpdateBulkWorkstep);
    },

    REPORT_SET_SELECTED_BULK_CONTENT: ({ selectedBulkContent }: { selectedBulkContent: DecoratedContent[] }) => {
      this.state.set('selectedBulkContent', selectedBulkContent);
    },

    REPORT_SET_BULK_SUMMARY: ({ bulkSummary }: { bulkSummary: ReportContentSummary }) => {
      this.state.set('bulkSummary', bulkSummary);
    },

    REPORT_SET_BULK_HIDE_UNCERTAINTY: ({
      bulkHideUncertainty,
    }: {
      bulkHideUncertainty: boolean | null | undefined;
    }) => {
      this.state.set('bulkHideUncertainty', bulkHideUncertainty);
    },

    REPORT_SET_BULK_ASSET_SELECTION: ({ bulkAssetSelection }: { bulkAssetSelection: AssetSelection | undefined }) => {
      this.state.set('bulkAssetSelection', bulkAssetSelection);
      if (this.state.get('bulkImpliedSelectionRemoval') === 'asset') {
        this.state.set('bulkImpliedSelectionRemoval', undefined);
      }
      // Content cannot use an asset selection and a row selection at once, so choosing one removes the other. A row
      // selection the user had already chosen to remove stays an explicit removal. Keeping the current asset
      // selections also keeps the current row selections, since attaching a row selection would remove them.
      const bulkRowSelectionId = this.state.get('bulkRowSelectionId');
      if (bulkAssetSelection && bulkAssetSelection.id !== KEEP_CURRENT_ASSET_SELECTION.id) {
        if (!_.isUndefined(bulkRowSelectionId)) {
          this.state.set('bulkRowSelectionId', undefined);
          this.state.set('bulkImpliedSelectionRemoval', 'row');
        }
      } else if (this.state.get('bulkImpliedSelectionRemoval') === 'row') {
        this.state.set('bulkRowSelectionId', KEEP_CURRENT_KEY);
        this.state.set('bulkImpliedSelectionRemoval', undefined);
      } else if (bulkAssetSelection && bulkRowSelectionId && bulkRowSelectionId !== KEEP_CURRENT_KEY) {
        this.state.set('bulkRowSelectionId', KEEP_CURRENT_KEY);
      }
    },

    REPORT_SET_BULK_ROW_SELECTION: ({ bulkRowSelectionId }: { bulkRowSelectionId: string | undefined }) => {
      this.setBulkRowSelectionId(bulkRowSelectionId);
    },

    REPORT_SET_SHOULD_SHOW_CONFIGURE_AUTO_UPDATE_MODAL: ({
      shouldShowConfigureAutoUpdateModal,
    }: {
      shouldShowConfigureAutoUpdateModal: boolean;
    }) => {
      this.state.set('shouldShowConfigureAutoUpdateModal', shouldShowConfigureAutoUpdateModal);
    },

    REPORT_SET_SHOW_ASSET_SWAP_MODAL: (showModal: boolean) => {
      this.state.set('showChooseAssetSwapModal', showModal);
    },

    REPORT_SET_SWAP_RANGE_INFO: (payload: { currentSwap: DateRangeSwapInfo; potentialSwaps: DateRangeSwapInfo[] }) => {
      this.state.set('activeDateRangeSwapInfo', payload);
    },

    REPORT_SET_SHOW_CAPSULE_MODAL: (showModal: boolean) => {
      this.state.set('showChooseCapsuleModal', showModal);
    },

    REPORT_SET_SHOW_CONFIGURE_AUTO_UPDATE_MODAL: ({
      showConfigureAutoUpdateModal,
      reportScheduleOverride = false,
    }: {
      showConfigureAutoUpdateModal: boolean;
      reportScheduleOverride?: boolean;
    }) => {
      this.state.set('reportScheduleOverride', reportScheduleOverride);
      this.state.set('showConfigureAutoUpdateModal', showConfigureAutoUpdateModal);
    },

    REPORT_TOGGLE_SPECIFIC_SELECTED_CONTENT: (content: Content) => {
      const index = _.findIndex(this.state.get('selectedBulkContent'), ['id', content.id]);
      if (index >= 0) {
        this.state.splice('selectedBulkContent', [index, 1]);
      } else {
        this.state.push('selectedBulkContent', content);
      }
    },

    REPORT_UPDATE_CLEAR_BULK_PROPERTIES: () => {
      this.state.set('bulkEditDisplayMode', BulkEditMode.DO_NOT_SHOW);
      this.state.set('bulkInteractive', KEEP_CURRENT_INTERACTIVE);
      this.state.set('bulkShape', KEEP_CURRENT_SHAPE);
      this.state.set('bulkScale', KEEP_CURRENT_SCALE);
      this.state.set('bulkSize', KEEP_CURRENT_SIZE);
      this.state.set('selectedBulkContent', []);
      this.state.set('bulkHeight', 500);
      this.state.set('bulkWidth', 500);
      this.state.set('bulkDateRange', KEEP_CURRENT_DATE_RANGE);
      this.state.set('bulkAssetSelection', KEEP_CURRENT_ASSET_SELECTION);
      this.state.set('bulkRowSelectionId', KEEP_CURRENT_KEY);
      this.state.set('bulkImpliedSelectionRemoval', undefined);
      this.state.set('bulkSummary', KEEP_CURRENT_SUMMARY);
      this.state.unset('bulkHideUncertainty');
      this.state.set('shouldUpdateBulkWorkstep', false);
    },

    REPORT_IMAGE_STATE_CHANGED: () => {
      const count = this.state.get('imageStateChanges');
      this.state.set('imageStateChanges', count + 1);
    },

    REPORT_IFRAME_REFRESH_COUNT_CHANGED: () => {
      const count = this.state.get('iframeRefreshCounter');
      this.state.set('iframeRefreshCounter', count + 1);
    },

    /**
     * Sets the report
     *
     * @param {Object} report - the report object
     * @param {String} report.id - the report id
     * @param {String} report.name - the report name
     * @param {String} report.createdBy - the report createdBy name
     * @param {String} report.renderer - the report renderer name
     * @param {String} report.createdAt - the report createdAt timestamp
     * @param {String} report.updatedAt - the report updatedAt timestamp
     * @param {String} report.document - the report document
     * @param {String} report.comments - the report comments
     * @param {String} [report.cronSchedule] - the report cronSchedule
     * @param {boolean} [report.background] - whether report's schedule runs when there are no listeners to its channel
     * @param {String} [report.nextRunTime] - the report's next run time
     * @param {String} report.backups - the report backups
     */
    REPORT_SET: (report: AnnotationOutputV1) => {
      this.state.set('id', report.id);
      this.state.set('name', report.name);
      this.state.set('createdBy', report.createdBy);
      this.state.set('renderer', report.createdBy);
      this.state.set('createdAt', report.createdAt);
      this.state.set('updatedAt', report.updatedAt);
      this.state.set(
        'document',
        report.document?.replace(IMAGE_SRC_REGEX, (match, contentId) => `src="${this.getContentImageUrl(contentId)}"`),
      );
      this.setIfNotEqual('comments', report.replies);
      this.state.set('reportSchedule', _.pick(report, ['cronSchedule', 'background', 'enabled']));
      this.state.set('nextRunTime', report.nextRunTime);
      this.state.set('isFixedWidth', report.fixedWidth);
      this.state.set('isCkEnabled', report.ckEnabled);

      // Sort backups in reverse chronological order and add a formatted date string
      const backupTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
      this.state.set(
        'backups',
        _.chain(report.backups)
          // oxlint-disable-next-line typescript/no-unsafe-unary-minus
          .sortBy((backup) => -parseISODate(backup.backupDate!))
          .map((backup) => {
            return _.merge({}, backup, {
              formattedBackupDate: formatTime(
                parseISODate(backup.backupDate!),
                sqWorksheetStore.timezone,
                backupTimeFormat,
              ),
            });
          })
          .value(),
      );
    },

    /**
     * Set all asset selections. Will overwrite asset selections already in the store
     */
    REPORT_SET_ALL_ASSET_SELECTIONS: (payload: AssetSelection[]) => {
      this.setIfNotEqual('assetSelections', payload);
    },

    /**
     * Add or update a given asset selection
     */
    REPORT_SET_ASSET_SELECTION: (payload: AssetSelection) => {
      this.markSandboxModeModified();
      const index = _.findIndex(this.state.get('assetSelections'), {
        id: payload.id,
      });
      if (index >= 0) {
        this.state.set(['assetSelections', index], payload);
      } else {
        this.state.push('assetSelections', payload);
      }
    },

    /**
     * Set all row selections. Will overwrite row selections already in the store
     */
    REPORT_SET_ALL_ROW_SELECTIONS: (payload: RowSelection[]) => {
      this.setIfNotEqual('rowSelections', payload);
      // A save response can archive the bulk row selection before its socket update arrives
      this.keepCurrentBulkRowSelectionIfArchived();
    },

    /**
     * Add or update a given row selection
     */
    REPORT_SET_ROW_SELECTION: (payload: RowSelection) => {
      this.markSandboxModeModified();
      const index = this.rowSelections.findIndex((selection) => selection.id === payload.id);
      if (index >= 0) {
        this.state.set(['rowSelections', index], payload);
      } else {
        this.state.push('rowSelections', payload);
      }
      this.keepCurrentBulkRowSelectionIfArchived();
    },

    /**
     * Adds (or updates) an individual piece of Seeq Content in this store, for caching purposes.
     *
     * @param {Object} payload - Seeq Content
     * @param {String} payload.id - ID of Seeq Content
     */
    REPORT_SET_CONTENT: (payload: Content) => {
      const index = _.findIndex(this.state.get('content'), ['id', payload.id]);
      if (index >= 0) {
        this.state.set(['content', index], this.withLiveGeneratingState(payload));
      } else {
        this.state.push('content', payload);
      }
    },

    /**
     * Adds all Seeq Content items to the store, for caching purposes
     *
     * @param {Object[]} payload - array of Seeq Content items
     */
    REPORT_SET_ALL_CONTENT: (payload: Content[]) => {
      this.setIfNotEqual('content', payload);
    },

    /**
     * Replaces all Seeq Content items with a snapshot fetched from the backend while the report is open, keeping the
     * generating state that live screenshot messages set since the snapshot was requested.
     *
     * @param payload.content - The content items in the snapshot
     * @param payload.requestedAt - When the snapshot was requested
     */
    REPORT_SET_ALL_CONTENT_SNAPSHOT: ({ content, requestedAt }: { content: Content[]; requestedAt: number }) => {
      this.setIfNotEqual(
        'content',
        content.map((contentItem) => this.withLiveGeneratingState(contentItem, requestedAt)),
      );
    },

    /**
     * Updates the height and width of content with the rendered size. Content specified must have
     * .useSizeFromRender set to true.
     *
     * @param {string} payload.contentId - ID of content to update
     * @param {number} payload.height - height of rendered content, in pixels
     * @param {number} payload.width - width of rendered content, in pixels
     */
    REPORT_SET_CONTENT_RENDER_SIZE: ({
      contentId,
      height,
      width,
    }: {
      contentId: string;
      height: number;
      width: number;
    }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);

      if (index >= 0) {
        let content = this.state.get(['content', index]);
        if (content.useSizeFromRender) {
          content = { ...content, height, width };
          this.state.set(['content', index], content);
        }
      }
    },

    /**
     * Sets the flag on content for if it is currently getting regenerated using a job.
     *
     * @param contentId - ID of content to update
     * @param isGenerating - true if a job is kicked off to generate new content
     */
    REPORT_SET_IS_CONTENT_GENERATING: ({ contentId, isGenerating }: { contentId: string; isGenerating: boolean }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);

      if (index >= 0) {
        this.state.merge(['content', index], { isGenerating, generatingUpdatedAt: Date.now() });
      }
      if (!isGenerating) {
        // Content that was loaded out of view so that it would be generated now only needs to load its new result once
        // it is scrolled near
        this.setIfNotEqual(
          'contentIdsToLoadOutOfView',
          _.without(this.state.get('contentIdsToLoadOutOfView'), contentId),
        );
      }
    },

    /**
     * Sets the hash code for the given piece of content.
     *
     * @param {string} payload.contentId - ID of the content object to update
     * @param {string} payload.hashCode - The unique identifier for the current variant of the image
     */
    REPORT_SET_CONTENT_HASH_CODE: ({ contentId, hashCode }: { contentId: string; hashCode: string }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);
      if (index >= 0) {
        this.state.merge(['content', index], { hashCode });
        // Bizarrely, if we don't commit here, the CK Image component's useEffect hook which listens on the hashCode
        // will trigger every once in a while as opposed to every time. As utterly baffling as that is, I am not
        // able to explain why - Ryan L.
        this.state.commit();
      }
    },

    REPORT_SET_CONTENT_WARNING: ({ contentId, warning }: { contentId: string; warning: string }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);
      if (index >= 0) {
        this.state.set(['content', index, 'screenshotWarning'], warning);
      }
    },

    REPORT_SET_CONTENT_DATA_WARNING: ({ contentId, warning }: { contentId: string; warning: string | undefined }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);
      if (index >= 0) {
        this.state.set(['content', index, 'dataRenderWarning'], warning);
      }
    },

    /**
     * Updates the timezone for the given piece of content.
     *
     * @param {string} payload.contentId - ID of the content object to update
     * @param {string} payload.timezone - The name of the desired timezone
     */
    REPORT_UPDATE_CONTENT_TIMEZONE: ({ contentId, timezone }: { contentId: string; timezone: string }) => {
      const index = _.findIndex(this.state.get('content'), ['id', contentId]);
      if (index >= 0) {
        this.state.merge(['content', index], { timezone });
      }
    },

    /**
     *
     * @param payload - object containing:
     *  contentId - If falsy, all content in this worksheet will have showWarningMessage updated
     *  showWarningMessage - If true, warning message is shown for specified piece of content. If not, the warning
     *  message is not shown.
     */
    REPORT_SET_CONTENT_SHOW_WARNING_MESSAGE: (payload: { contentId: string; showWarningMessage: boolean }) => {
      if (!payload.contentId) {
        _.forEach(this.state.get('content'), (content) => {
          this.state.set(['contentShowWarningMessage', content.id], payload.showWarningMessage);
        });
      } else {
        if (payload.showWarningMessage) {
          this.state.set(['contentShowWarningMessage', payload.contentId], true);
        } else {
          this.state.unset(['contentShowWarningMessage', payload.contentId]);
        }
      }
    },

    /**
     * Removes all content from the store
     */
    REPORT_REMOVE_ALL_CONTENT: () => {
      this.state.set('content', []);
    },

    /**
     * Sets the report renderer
     *
     * @param {Object} payload - an object container
     * @param {Object} payload.renderer - the renderer identity
     */
    REPORT_SET_RENDERER: (payload: { renderer: string }) => {
      this.state.set('renderer', payload.renderer);
    },

    /**
     * Updates or adds the date variable in the list of date variables.
     *
     * @param {Object} dateRange - date range to set
     */
    REPORT_SET_DATE_RANGE: (dateRange: DateRange) => {
      this.markSandboxModeModified();
      // The range will be empty if this date range is defined by a capsule but no capsule was found.
      if (_.isEmpty(dateRange.range) && _.get(dateRange, 'auto.enabled')) {
        _.set(dateRange, 'auto.noCapsuleFound', true);
      }

      const index = _.findIndex(this.state.get('dateRanges'), ['id', dateRange.id]);

      if (index >= 0) {
        this.state.set(['dateRanges', index], dateRange);
      } else {
        this.state.push('dateRanges', dateRange);
      }
    },

    /**
     * Sets the list of date ranges
     *
     * @param dateRanges: list of all date ranges to set
     */
    REPORT_SET_ALL_DATE_RANGES: (dateRanges: DateRange[]) => {
      _.chain(dateRanges)
        .filter((dateRange) => _.isEmpty(dateRange.range) && _.get(dateRange, 'auto.enabled'))
        .forEach((dateRange) => _.set(dateRange, 'auto.noCapsuleFound', true))
        .value();

      this.setIfNotEqual('dateRanges', dateRanges);
    },

    /**
     * Removes all date ranges from the store
     */
    REPORT_REMOVE_ALL_DATE_RANGES: () => {
      this.state.set('dateRanges', []);
    },

    /**
     * Updates the backup preview
     *
     * @param {Object} payload - an object container
     * @param {Object} payload.backupPreview - The backup preview
     */
    REPORT_SET_BACKUP_PREVIEW: (payload: { backupPreview?: { backupName: string; document: string } }) => {
      this.state.set('backupPreview', payload.backupPreview);
    },

    /**
     * Set whether or not date ranges are being updated
     *
     * @param {Object} payload - an object container
     * @param {boolean} payload.dateRangeUpdating - true if a date range is updating, false if not.
     */
    REPORT_SET_DATE_RANGE_UPDATING: (payload: { dateRangeUpdating: boolean }) => {
      this.state.set('dateRangeUpdating', payload.dateRangeUpdating);
    },

    /**
     * Handles events that may update the editing state.
     */
    REPORT_EDITING_STATE_EVENT: (payload: { event: ReportEditingStateEvent }) => {
      const transitions: Record<ReportEditingStateEvent, (state: ReportEditingState) => ReportEditingState> = {
        [ReportEditingStateEvent.SaveStarted]: (state: ReportEditingState) =>
          state === ReportEditingState.Offline ? ReportEditingState.Offline : ReportEditingState.Saving,
        [ReportEditingStateEvent.SaveComplete]: (_state: ReportEditingState) => ReportEditingState.Saved,
        [ReportEditingStateEvent.Online]: (state: ReportEditingState) =>
          state === ReportEditingState.Offline ? ReportEditingState.Stale : state,
        [ReportEditingStateEvent.Offline]: (_state: ReportEditingState) => ReportEditingState.Offline,
      };
      const currentState = this.state.get('editingState');
      this.state.set('editingState', transitions[payload.event](currentState));
    },

    /**
     * Update the start and end times for the given date range.
     * @param {String} dateRangeId
     * @param {String} start
     * @param {String} end
     */
    REPORT_UPDATE_RANGE_START_AND_END: ({
      dateRangeId,
      start,
      end,
    }: {
      dateRangeId: string;
      start: number;
      end: number;
    }) => {
      const index = _.findIndex(this.state.get('dateRanges'), ['id', dateRangeId]);
      if (index >= 0) {
        this.state.merge(['dateRanges', index, 'range'], { start, end });
      }
    },

    /**
     * Sets the given date range's "no capsule found" flag to the desired value (true, by default)
     * @param {String} dateRangeId
     * @param {Boolean} value
     */
    REPORT_UPDATE_NO_CAPSULE_FOUND: ({ dateRangeId, value }: { dateRangeId: string; value: boolean }) => {
      const index = _.findIndex(this.state.get('dateRanges'), ['id', dateRangeId]);
      if (index >= 0) {
        this.state.merge(['dateRanges', index, 'auto'], {
          noCapsuleFound: value,
        });
      }
    },

    /**
     * Makes content load even while it is out of view, rather than waiting until it is scrolled near. It still only
     * renders once it is near the viewport.
     *
     * @param contentIds - IDs of the content to load
     */
    REPORT_LOAD_CONTENT_OUT_OF_VIEW: (contentIds: string[]) => {
      this.setIfNotEqual('contentIdsToLoadOutOfView', _.union(this.state.get('contentIdsToLoadOutOfView'), contentIds));
    },

    /**
     * Clears the report store in preparation for loading a new report.
     */
    REPORT_RESET: () => {
      this.initialize('FORCE');
    },

    /**
     * Adds (or updates) a comment
     *
     * @param {Object} comment - Seeq comment
     */
    REPORT_SET_COMMENT: (comment: AnnotationOutputV1) => {
      const index = _.findIndex(this.state.get('comments'), ['id', comment.id]);
      if (index >= 0) {
        this.state.set(['comments', index], comment);
      } else {
        this.state.push('comments', comment);
      }
    },

    /**
     * Removes a comment
     *
     * @param {Object} id - id of the content
     */
    REPORT_REMOVE_COMMENT: (id: string) => {
      const index = _.findIndex(this.state.get('comments'), ['id', id]);

      if (index >= 0) {
        this.state.splice('comments', [index, 1]);
      }
    },

    REPORT_SET_REPORT_SCHEDULE: (reportSchedule: ReportSchedule | undefined) => {
      this.state.set('reportScheduleError', undefined);
      this.state.set('reportSchedule', reportSchedule);
    },

    REPORT_SET_NEXT_RUN_TIME: (formattedTime: string | undefined) => {
      this.state.set('nextRunTime', formattedTime);
    },

    REPORT_SCHEDULED_UPDATE_RECEIVED: () => {
      const count = this.state.get('scheduledUpdateCount');
      this.state.set('scheduledUpdateCount', count + 1);
    },

    REPORT_SET_IS_FIXED_WIDTH: (payload: { isFixedWidth: boolean }) => {
      this.state.set('isFixedWidth', payload.isFixedWidth);
    },

    REPORT_SET_IS_SANDBOX_DISABLED: ({ isDisabled }: { isDisabled: boolean }) => {
      this.state.set('isSandboxDisabled', isDisabled);
    },

    REPORT_SET_SANDBOX_MODE: (payload: SandboxMode) => {
      this.state.set('sandboxMode', payload);
    },

    REPORT_SET_CONTENT_DISPLAY_METADATA: (payload: { contentId: string; displayMetadata: ContentDisplayMetadata }) => {
      const index = _.findIndex(this.state.get('contentDisplayMetadata'), ['contentId', payload.contentId]);
      if (index >= 0) {
        this.state.set(['contentDisplayMetadata', index], payload);
      } else {
        this.state.push('contentDisplayMetadata', payload);
      }
    },

    REPORT_SET_REPORT_SCHEDULE_ERROR: (payload: { error: string; errorCode: number }) => {
      this.state.set('reportScheduleError', payload);
    },

    REPORT_REMOVE_CONTENT_DISPLAY_METADATA: (contentId: string) => {
      const index = _.findIndex(this.state.get('contentDisplayMetadata'), ['contentId', contentId]);

      if (index >= 0) {
        this.state.splice('contentDisplayMetadata', [index, 1]);
      }
    },

    REPORT_ADD_CONTENT_ERROR: ({ contentId }: { contentId: string }) => {
      if (!_.some(this.state.get('contentErrors'), (el) => el === contentId)) {
        this.state.push('contentErrors', contentId);
      }
    },

    REPORT_RESET_CONTENT_ERRORS: () => {
      this.state.set('contentErrors', []);
    },
  };
}
